import type { SearchableValuePickerOption } from "@/components/shared/searchable-value-picker";
import type {
  ConnectorInstallResponse,
  ProviderDescriptorRecordDto,
  WorkflowDefinitionDto,
} from "@/lib/api/generated/control-api.schemas";
import type {
  JsonObject,
  TenantCapabilityBindingRecord,
  TenantCapabilityTargetRecord,
} from "../../../../../packages/shared-types/src/index.js";
import type { PolicyFormState } from "./agent-policy-utils";
import { humanizeProviderLabel } from "./agent-policy-utils";

export type RuntimeConnectorAccessDraft = {
  allowedBindingIds: string[];
  allowedTargetKeysByBindingId: Record<string, string[]>;
  defaultTargetKeyByBindingId: Record<string, string>;
};

export type AgentPolicyWorkflowOption = {
  workflowKey: string;
  name: string;
  description: string;
};

export type AgentPolicyConnectorRow = {
  id: string;
  kind: "graphrag_source" | "connector_binding";
  connectorKey: string;
  connectorLabel: string;
  bindingId: string;
  targetKey?: string;
  title: string;
  description: string;
  supportingText: string;
  toolName: string;
  toolNames: string[];
  isSelected: boolean;
  isDefault: boolean;
};

export type AgentPolicyConnectorSection = {
  id: string;
  title: string;
  description: string;
  rows: AgentPolicyConnectorRow[];
};

export type AgentPolicyViewModel = {
  workflowOptions: AgentPolicyWorkflowOption[];
  workflowPickerOptions: SearchableValuePickerOption[];
  connectorSections: AgentPolicyConnectorSection[];
  allowedToolOptions: SearchableValuePickerOption[];
  dangerousToolOptions: SearchableValuePickerOption[];
  unresolvedConnectorKeys: string[];
  unresolvedAccess: RuntimeConnectorAccessDraft;
};

const CORE_ALLOWED_TOOL_OPTIONS: SearchableValuePickerOption[] = [
  { value: "message", label: "message", hint: "Send messages and channel actions", group: "Core" },
  { value: "sessions_list", label: "sessions_list", hint: "List sessions", group: "Core" },
  { value: "sessions_history", label: "sessions_history", hint: "Read session history", group: "Core" },
  { value: "sessions_send", label: "sessions_send", hint: "Send to another session", group: "Core" },
  { value: "session_status", label: "session_status", hint: "Session status card", group: "Core" },
  { value: "memory_search", label: "memory_search", hint: "Semantic memory search", group: "Core" },
  { value: "memory_get", label: "memory_get", hint: "Read memory files", group: "Core" },
  { value: "web_search", label: "web_search", hint: "Search the web", group: "Core" },
  { value: "web_fetch", label: "web_fetch", hint: "Fetch web content", group: "Core" },
  { value: "cron", label: "cron", hint: "Manage schedules and reminders", group: "Core" },
  { value: "read", label: "read", hint: "Read file contents", group: "Core" },
  { value: "write", label: "write", hint: "Create or overwrite files", group: "Core" },
  { value: "edit", label: "edit", hint: "Apply precise edits", group: "Core" },
  { value: "apply_patch", label: "apply_patch", hint: "Apply patches", group: "Core" },
  { value: "exec", label: "exec", hint: "Execute shell commands", group: "Core" },
];

function asBindingRecord(
  value: unknown,
): TenantCapabilityBindingRecord | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  return value as TenantCapabilityBindingRecord;
}

function asTargetRecord(
  value: unknown,
): TenantCapabilityTargetRecord | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  return value as TenantCapabilityTargetRecord;
}

function readString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function readMetadataString(metadata: JsonObject | undefined, key: string): string {
  const value = metadata?.[key];
  return typeof value === "string" ? value.trim() : "";
}

function uniqueSorted(values: Iterable<string>): string[] {
  return Array.from(new Set(Array.from(values).map((value) => value.trim()).filter(Boolean))).sort(
    (left: string, right: string) => left.localeCompare(right),
  );
}

function groupLabelForConnector(connectorKey: string): string {
  return humanizeProviderLabel(connectorKey) || connectorKey;
}

function buildConnectorCommandName(capabilityKey: string, operationKey: string): string {
  return capabilityKey.endsWith(`.${operationKey}`)
    ? capabilityKey
    : `${capabilityKey}.${operationKey}`;
}

function toConnectorCallableToolName(commandName: string): string {
  return commandName.replace(/\./g, "_");
}

function inferOperationType(operationKey: string): string {
  const normalized = operationKey.trim().toLowerCase();
  if (normalized === "prepare" || normalized === "submit" || normalized === "verify") {
    return normalized;
  }
  return "read";
}

function slugifyToolToken(value: string, fallback: string): string {
  const token = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return token.length > 0 ? token : fallback;
}

function listConnectorCallableToolNames(
  binding: TenantCapabilityBindingRecord,
  descriptor?: ProviderDescriptorRecordDto,
  target?: TenantCapabilityTargetRecord,
): string[] {
  const capability = descriptor?.capabilities?.find(
    (entry) => entry.capabilityKey === binding.capabilityKey,
  );
  const operationNames = (capability?.operations ?? [])
    .filter(
      (operation) =>
        (operation.operationType?.trim() ?? inferOperationType(operation.operationKey)) !== "submit",
    )
    .map((operation) => operation.operationKey.trim())
    .filter((operationKey) => operationKey.length > 0);
  return operationNames.map((operationKey) => {
    const commandName = buildConnectorCommandName(binding.capabilityKey, operationKey);
    if (target && (binding.providerKey === "graphrag" || descriptor?.providerKey === "graphrag")) {
      const alias = readMetadataString(target.metadata, "toolAlias");
      const basis = alias || target.displayName || target.targetKey;
      return toConnectorCallableToolName(`${commandName}.${slugifyToolToken(basis, "target")}`);
    }
    return toConnectorCallableToolName(commandName);
  });
}

function buildWorkflowOptions(
  workflowDefinitions: WorkflowDefinitionDto[] | undefined,
  enabledWorkflowKeys: string[],
): AgentPolicyWorkflowOption[] {
  const optionByKey = new Map<string, AgentPolicyWorkflowOption>();

  for (const definition of workflowDefinitions ?? []) {
    const workflowKey = readString(definition.workflowKey);
    if (!workflowKey) {
      continue;
    }
    optionByKey.set(workflowKey, {
      workflowKey,
      name: readString(definition.name) || workflowKey,
      description: readString(definition.description),
    });
  }

  for (const workflowKey of enabledWorkflowKeys) {
    const normalized = readString(workflowKey);
    if (!normalized || optionByKey.has(normalized)) {
      continue;
    }
    optionByKey.set(normalized, {
      workflowKey: normalized,
      name: normalized,
      description: "Existing workflow selection not present in current tenant definitions.",
    });
  }

  return [...optionByKey.values()].sort((left, right) =>
    left.name.localeCompare(right.name),
  );
}

function buildWorkflowPickerOptions(
  workflowOptions: AgentPolicyWorkflowOption[],
): SearchableValuePickerOption[] {
  return workflowOptions.map((option) => ({
    value: option.workflowKey,
    label: option.name,
    hint: option.description || undefined,
    group: "Workflow",
  }));
}

export function parseRuntimeConnectorAccessJson(value: string): RuntimeConnectorAccessDraft {
  try {
    const parsed = JSON.parse(value) as Partial<RuntimeConnectorAccessDraft>;
    const allowedBindingIds = uniqueSorted(parsed.allowedBindingIds ?? []);
    const allowedTargetKeysByBindingId: Record<string, string[]> = {};
    for (const bindingId of allowedBindingIds) {
      allowedTargetKeysByBindingId[bindingId] = uniqueSorted(
        parsed.allowedTargetKeysByBindingId?.[bindingId] ?? [],
      );
    }

    const defaultTargetKeyByBindingId: Record<string, string> = {};
    for (const bindingId of allowedBindingIds) {
      const defaultTargetKey = readString(parsed.defaultTargetKeyByBindingId?.[bindingId]);
      if (
        defaultTargetKey &&
        allowedTargetKeysByBindingId[bindingId]?.includes(defaultTargetKey)
      ) {
        defaultTargetKeyByBindingId[bindingId] = defaultTargetKey;
      }
    }

    return {
      allowedBindingIds,
      allowedTargetKeysByBindingId,
      defaultTargetKeyByBindingId,
    };
  } catch {
    return {
      allowedBindingIds: [],
      allowedTargetKeysByBindingId: {},
      defaultTargetKeyByBindingId: {},
    };
  }
}

export function buildAgentPolicyViewModel(params: {
  form: PolicyFormState;
  capabilityBindings: unknown[] | undefined;
  capabilityTargets: unknown[] | undefined;
  connectorInstalls: ConnectorInstallResponse[] | undefined;
  providerDescriptors?: ProviderDescriptorRecordDto[] | undefined;
  workflowDefinitions: WorkflowDefinitionDto[] | undefined;
}): AgentPolicyViewModel {
  const access = parseRuntimeConnectorAccessJson(params.form.runtimeConnectorAccessJson);
  const bindings = (params.capabilityBindings ?? [])
    .map(asBindingRecord)
    .filter((entry): entry is TenantCapabilityBindingRecord => entry !== null)
    .filter((entry) => entry.status === "active");
  const targets = (params.capabilityTargets ?? [])
    .map(asTargetRecord)
    .filter((entry): entry is TenantCapabilityTargetRecord => entry !== null)
    .filter((entry) => entry.status === "active");
  const installById = new Map(
    (params.connectorInstalls ?? []).map((install) => [install.id, install] as const),
  );
  const descriptorByProviderKey = new Map(
    (params.providerDescriptors ?? [])
      .filter((descriptor) => descriptor.executionHost === "runtime_connector")
      .map((descriptor) => [descriptor.providerKey, descriptor] as const),
  );
  const selectedConnectorKeys = new Set(params.form.enabledConnectorKeys.map((entry) => entry.trim()));
  const knownBindingIds = new Set(bindings.map((binding) => binding.id));
  const workflowOptions = buildWorkflowOptions(
    params.workflowDefinitions,
    params.form.enabledWorkflowKeys,
  );

  const targetsByBindingId = new Map<string, TenantCapabilityTargetRecord[]>();
  for (const target of targets) {
    const existing = targetsByBindingId.get(target.bindingId) ?? [];
    existing.push(target);
    targetsByBindingId.set(target.bindingId, existing);
  }

  const rowsByConnectorKey = new Map<string, AgentPolicyConnectorRow[]>();
  for (const binding of bindings) {
    const install = binding.connectorInstallId
      ? installById.get(binding.connectorInstallId)
      : undefined;
    const connectorKey = readString(install?.connectorKey) || readString(binding.providerKey);
    if (!connectorKey) {
      continue;
    }

    if (connectorKey === "graphrag") {
      const descriptor = descriptorByProviderKey.get(binding.providerKey);
      for (const target of targetsByBindingId.get(binding.id) ?? []) {
        const toolNames = listConnectorCallableToolNames(binding, descriptor, target);
        const toolAlias = readMetadataString(target.metadata, "toolAlias");
        const toolDescription = readMetadataString(target.metadata, "toolDescription");
        const selected =
          selectedConnectorKeys.has(connectorKey) &&
          access.allowedBindingIds.includes(binding.id) &&
          (access.allowedTargetKeysByBindingId[binding.id] ?? []).includes(target.targetKey);
        const row: AgentPolicyConnectorRow = {
          id: `${binding.id}:${target.targetKey}`,
          kind: "graphrag_source",
          connectorKey,
          connectorLabel: groupLabelForConnector(connectorKey),
          bindingId: binding.id,
          targetKey: target.targetKey,
          title: toolAlias || target.displayName,
          description:
            toolDescription ||
            `Evidence retrieval scoped to workspace ${target.providerTargetRef}.`,
          supportingText: `${target.displayName} · ${target.targetKey} · ${target.providerTargetRef}`,
          toolName: toolNames[0] ?? binding.capabilityKey,
          toolNames,
          isSelected: selected,
          isDefault:
            selected &&
            access.defaultTargetKeyByBindingId[binding.id] === target.targetKey,
        };
        const existing = rowsByConnectorKey.get(connectorKey) ?? [];
        existing.push(row);
        rowsByConnectorKey.set(connectorKey, existing);
      }
      continue;
    }

    const toolNames = listConnectorCallableToolNames(
      binding,
      descriptorByProviderKey.get(binding.providerKey),
    );
    const row: AgentPolicyConnectorRow = {
      id: binding.id,
      kind: "connector_binding",
      connectorKey,
      connectorLabel: groupLabelForConnector(connectorKey),
      bindingId: binding.id,
      title: binding.capabilityKey,
      description:
        `${groupLabelForConnector(connectorKey)} capability binding` +
        (binding.defaultTargetKey ? ` with default target ${binding.defaultTargetKey}` : "."),
      supportingText: [binding.descriptorKey, binding.providerKey, binding.id]
        .map((entry) => readString(entry))
        .filter(Boolean)
        .join(" · "),
      toolName: toolNames[0] ?? binding.capabilityKey,
      toolNames,
      isSelected:
        selectedConnectorKeys.has(connectorKey) || access.allowedBindingIds.includes(binding.id),
      isDefault: false,
    };
    const existing = rowsByConnectorKey.get(connectorKey) ?? [];
    existing.push(row);
    rowsByConnectorKey.set(connectorKey, existing);
  }

  const connectorSections = [...rowsByConnectorKey.entries()]
    .map(([connectorKey, rows]) => ({
      id: connectorKey,
      title: groupLabelForConnector(connectorKey),
      description:
        connectorKey === "graphrag"
          ? "Concrete knowledge sources exposed as separate agent tools."
          : "Connector-backed capabilities available to this agent.",
      rows: [...rows].sort((left: AgentPolicyConnectorRow, right: AgentPolicyConnectorRow) =>
        left.title.localeCompare(right.title),
      ),
    }))
    .sort((left: AgentPolicyConnectorSection, right: AgentPolicyConnectorSection) =>
      left.title.localeCompare(right.title),
    );

  const unresolvedConnectorKeys = params.form.enabledConnectorKeys
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0 && !rowsByConnectorKey.has(entry));

  const unresolvedAccess: RuntimeConnectorAccessDraft = {
    allowedBindingIds: access.allowedBindingIds.filter((bindingId) => !knownBindingIds.has(bindingId)),
    allowedTargetKeysByBindingId: {},
    defaultTargetKeyByBindingId: {},
  };
  for (const bindingId of unresolvedAccess.allowedBindingIds) {
    unresolvedAccess.allowedTargetKeysByBindingId[bindingId] =
      access.allowedTargetKeysByBindingId[bindingId] ?? [];
    const defaultTargetKey = access.defaultTargetKeyByBindingId[bindingId];
    if (defaultTargetKey) {
      unresolvedAccess.defaultTargetKeyByBindingId[bindingId] = defaultTargetKey;
    }
  }

  const dangerousToolOptions = connectorSections
    .flatMap((section: AgentPolicyConnectorSection) => section.rows)
    .flatMap((row) =>
      row.toolNames.map((toolName) => ({
        value: toolName,
        label: row.toolNames.length === 1 ? row.title : `${row.title} · ${toolName}`,
        hint: row.description,
        group: row.connectorLabel,
      })),
    )
    .sort(
      (left: SearchableValuePickerOption, right: SearchableValuePickerOption) =>
        left.value.localeCompare(right.value),
    );

  const allowedToolOptions = Array.from([
    ...CORE_ALLOWED_TOOL_OPTIONS,
    ...dangerousToolOptions,
  ]
    .reduce((map, option) => {
      const normalized = option.value.trim();
      if (!normalized || map.has(normalized)) {
        return map;
      }
      map.set(normalized, {
        ...option,
        value: normalized,
      });
      return map;
    }, new Map<string, SearchableValuePickerOption>())
    .values()).sort((left, right) => left.value.localeCompare(right.value));

  return {
    workflowOptions,
    workflowPickerOptions: buildWorkflowPickerOptions(workflowOptions),
    connectorSections,
    allowedToolOptions,
    dangerousToolOptions,
    unresolvedConnectorKeys,
    unresolvedAccess,
  };
}

function mergeAccess(
  unresolvedAccess: RuntimeConnectorAccessDraft,
  selectedRows: AgentPolicyConnectorRow[],
): RuntimeConnectorAccessDraft {
  const allowedBindingIds = new Set(unresolvedAccess.allowedBindingIds);
  const allowedTargetKeysByBindingId: Record<string, string[]> = {
    ...unresolvedAccess.allowedTargetKeysByBindingId,
  };
  const defaultTargetKeyByBindingId: Record<string, string> = {
    ...unresolvedAccess.defaultTargetKeyByBindingId,
  };

  for (const row of selectedRows) {
    allowedBindingIds.add(row.bindingId);
    if (row.kind !== "graphrag_source" || !row.targetKey) {
      continue;
    }
    const nextTargets = new Set(allowedTargetKeysByBindingId[row.bindingId] ?? []);
    nextTargets.add(row.targetKey);
    allowedTargetKeysByBindingId[row.bindingId] = uniqueSorted(nextTargets);
    if (row.isDefault || !defaultTargetKeyByBindingId[row.bindingId]) {
      defaultTargetKeyByBindingId[row.bindingId] = row.targetKey;
    }
  }

  for (const bindingId of Array.from(allowedBindingIds)) {
    const targetKeys = allowedTargetKeysByBindingId[bindingId] ?? [];
    if (targetKeys.length === 0) {
      delete allowedTargetKeysByBindingId[bindingId];
      delete defaultTargetKeyByBindingId[bindingId];
      continue;
    }
    const defaultTargetKey = defaultTargetKeyByBindingId[bindingId];
    if (!defaultTargetKey || !targetKeys.includes(defaultTargetKey)) {
      defaultTargetKeyByBindingId[bindingId] = targetKeys[0]!;
    }
  }

  return {
    allowedBindingIds: uniqueSorted(allowedBindingIds),
    allowedTargetKeysByBindingId,
    defaultTargetKeyByBindingId,
  };
}

function selectedRowsAfterToggle(
  model: AgentPolicyViewModel,
  rowId: string,
  enabled: boolean,
): AgentPolicyConnectorRow[] {
  return model.connectorSections
    .flatMap((section) => section.rows)
    .map((row) => (row.id === rowId ? { ...row, isSelected: enabled } : row))
    .filter((row) => row.isSelected);
}

export function applyConnectorRowEnabled(params: {
  form: PolicyFormState;
  model: AgentPolicyViewModel;
  rowId: string;
  enabled: boolean;
}): Pick<PolicyFormState, "enabledConnectorKeys" | "runtimeConnectorAccessJson"> {
  const selectedRows = selectedRowsAfterToggle(params.model, params.rowId, params.enabled);
  const selectedConnectorKeys = uniqueSorted([
    ...params.model.unresolvedConnectorKeys,
    ...selectedRows.map((row) => row.connectorKey),
  ]);
  const nextAccess = mergeAccess(params.model.unresolvedAccess, selectedRows);

  return {
    enabledConnectorKeys: selectedConnectorKeys,
    runtimeConnectorAccessJson: JSON.stringify(nextAccess, null, 2),
  };
}

export function applyConnectorDefaultTarget(params: {
  model: AgentPolicyViewModel;
  bindingId: string;
  targetKey: string;
}): Pick<PolicyFormState, "runtimeConnectorAccessJson"> {
  const selectedRows = params.model.connectorSections
    .flatMap((section) => section.rows)
    .filter((row) => row.isSelected)
    .map((row) =>
      row.bindingId === params.bindingId && row.targetKey === params.targetKey
        ? { ...row, isDefault: true }
        : row.bindingId === params.bindingId
          ? { ...row, isDefault: false }
          : row,
    );
  const nextAccess = mergeAccess(params.model.unresolvedAccess, selectedRows);
  return {
    runtimeConnectorAccessJson: JSON.stringify(nextAccess, null, 2),
  };
}
